/// <reference lib="dom" />
import { useEffect, useState } from "hono/jsx";
import type { QuestionFormat } from "@/lib/kvdex/models.ts";

interface Question {
  id: string;
  ownerId: string;
  text: string;
  answer: string;
  points: number;
  tags: string[];
  format: QuestionFormat;
  mediaUrl?: string;
  isPublic: boolean;
  version: number;
  createdAt: number;
  updatedAt: number;
}

interface QuestionSet {
  id: string;
  name: string;
  description?: string;
  questionIds: string[];
  isPublic: boolean;
  version: number;
  createdAt: number;
  updatedAt: number;
}

interface QuizAdminProps {
  user: { id: string; name?: string };
}

export const QuizAdmin = ({ user }: QuizAdminProps) => {
  const [activeTab, setActiveTab] = useState<"questions" | "sets">("questions");
  const [questions, setQuestions] = useState<
    { my: Question[]; public: Question[] }
  >({ my: [], public: [] });
  const [sets, setSets] = useState<
    { my: QuestionSet[]; public: QuestionSet[] }
  >({ my: [], public: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set Form States
  const [showNewSet, setShowNewSet] = useState(false);
  const [editingSet, setEditingSet] = useState<QuestionSet | null>(null);
  const [newSetName, setNewSetName] = useState("");
  const [newSetDesc, setNewSetDesc] = useState("");
  const [newSetQuestions, setNewSetQuestions] = useState<string[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        fetch("/api/quiz/questions"),
        fetch("/api/quiz/sets"),
      ]);
      if (qRes.ok) setQuestions(await qRes.json());
      if (sRes.ok) setSets(await sRes.json());
    } catch (_e) {
      setError("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Question Actions
  const deleteQuestion = async (id: string) => {
    if (!confirm("Delete this question?")) return;
    const res = await fetch(`/api/quiz/questions/${id}`, { method: "DELETE" });
    if (res.ok) fetchData();
    else setError("Failed to delete question");
  };

  const publishQuestion = async (id: string) => {
    const res = await fetch(`/api/quiz/questions/${id}/publish`, {
      method: "POST",
    });
    if (res.ok) fetchData();
    else setError("Failed to publish question");
  };

  // Set Actions
  const createSet = async () => {
    const res = await fetch("/api/quiz/sets", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: newSetName,
        description: newSetDesc,
        questionIds: newSetQuestions,
      }),
    });
    if (res.ok) {
      setNewSetName("");
      setNewSetDesc("");
      setNewSetQuestions([]);
      setShowNewSet(false);
      fetchData();
    } else {
      setError("Failed to create set");
    }
  };

  const updateSet = async () => {
    if (!editingSet) return;
    const res = await fetch(`/api/quiz/sets/${editingSet.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: newSetName,
        description: newSetDesc,
        questionIds: newSetQuestions,
      }),
    });
    if (res.ok) {
      setEditingSet(null);
      setNewSetName("");
      setNewSetDesc("");
      setNewSetQuestions([]);
      fetchData();
    } else {
      setError("Failed to update set");
    }
  };

  const deleteSet = async (id: string) => {
    if (!confirm("Delete this set?")) return;
    const res = await fetch(`/api/quiz/sets/${id}`, { method: "DELETE" });
    if (res.ok) fetchData();
    else setError("Failed to delete set");
  };

  const publishSet = async (id: string) => {
    const res = await fetch(`/api/quiz/sets/${id}/publish`, { method: "POST" });
    if (res.ok) fetchData();
    else setError("Failed to publish set");
  };

  const startEditSet = (s: QuestionSet) => {
    setEditingSet(s);
    setNewSetName(s.name);
    setNewSetDesc(s.description || "");
    setNewSetQuestions(s.questionIds);
  };

  const toggleQuestionInSet = (qId: string) => {
    setNewSetQuestions((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  if (loading) {
    return (
      <div class="min-h-screen flex items-center justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div class="min-h-screen bg-base-200 p-6">
      <div class="max-w-6xl mx-auto">
        <div class="flex items-center justify-between mb-8">
          <div>
            <h1 class="text-3xl font-black">Quiz Admin</h1>
            <p class="opacity-60">Manage questions and sets</p>
          </div>
          <a href="/quiz" class="btn btn-ghost">Back to Lobby</a>
        </div>

        {error && (
          <div class="alert alert-error mb-4">
            <span>{error}</span>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => setError(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        <div class="tabs tabs-boxed mb-6">
          <button
            type="button"
            class={`tab ${activeTab === "questions" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("questions")}
          >
            Questions ({questions.my.length})
          </button>
          <button
            type="button"
            class={`tab ${activeTab === "sets" ? "tab-active" : ""}`}
            onClick={() => setActiveTab("sets")}
          >
            Sets ({sets.my.length})
          </button>
        </div>

        {activeTab === "questions" && (
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-bold">My Questions</h2>
              <a href="/quiz/admin/questions/new" class="btn btn-primary">
                + New Question
              </a>
            </div>

            <div class="overflow-x-auto">
              <table class="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Format</th>
                    <th>Question</th>
                    <th>Answer</th>
                    <th>Points</th>
                    <th>Tags</th>
                    <th>Public</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.my.map((q) => (
                    <tr key={q.id}>
                      <td>
                        <div class="badge badge-sm badge-ghost">
                          {q.format || "txt"}
                        </div>
                      </td>
                      <td class="max-w-xs truncate">
                        <div class="flex items-center gap-2">
                          {q.format === "svg" && q.mediaUrl
                            ? (
                              <>
                                <img
                                  src={q.mediaUrl}
                                  alt="Visual"
                                  class="h-8 w-8 object-contain bg-white rounded border"
                                />
                                <span class="text-xs text-base-content/50 italic ml-2">
                                  SVG Image
                                </span>
                              </>
                            )
                            : <span class="truncate">{q.text}</span>}
                        </div>
                      </td>
                      <td>{q.answer}</td>
                      <td>{q.points}</td>
                      <td>
                        {q.tags.map((t) => (
                          <span key={t} class="badge badge-sm mr-1">{t}</span>
                        ))}
                      </td>
                      <td>{q.isPublic ? "Yes" : "No"}</td>
                      <td class="flex gap-1">
                        <a
                          href={`/quiz/admin/questions/${q.id}`}
                          class="btn btn-ghost btn-xs"
                        >
                          Edit
                        </a>
                        {!q.isPublic && (
                          <button
                            type="button"
                            class="btn btn-ghost btn-xs"
                            onClick={() => publishQuestion(q.id)}
                          >
                            Publish
                          </button>
                        )}
                        <button
                          type="button"
                          class="btn btn-ghost btn-xs text-error"
                          onClick={() => deleteQuestion(q.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {questions.public.length > 0 && (
              <>
                <h2 class="text-xl font-bold mt-8">Public Questions</h2>
                <div class="overflow-x-auto">
                  <table class="table table-zebra w-full">
                    <thead>
                      <tr>
                        <th>Question</th>
                        <th>Answer</th>
                        <th>Points</th>
                        <th>Tags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.public.map((q) => (
                        <tr key={q.id}>
                          <td class="max-w-xs truncate">{q.text}</td>
                          <td>{q.answer}</td>
                          <td>{q.points}</td>
                          <td>
                            {q.tags.map((t) => (
                              <span key={t} class="badge badge-sm mr-1">
                                {t}
                              </span>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "sets" && (
          <div class="space-y-4">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-bold">My Question Sets</h2>
              <button
                type="button"
                class="btn btn-primary"
                onClick={() => {
                  setShowNewSet(true);
                  setEditingSet(null);
                  setNewSetName("");
                  setNewSetDesc("");
                  setNewSetQuestions([]);
                }}
              >
                + New Set
              </button>
            </div>

            {(showNewSet || editingSet) && (
              <div class="card bg-base-100 shadow-xl">
                <div class="card-body">
                  <h3 class="card-title">
                    {editingSet ? "Edit Set" : "New Set"}
                  </h3>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">Set Name</span>
                    </label>
                    <input
                      type="text"
                      class="input input-bordered"
                      value={newSetName}
                      onInput={(e) =>
                        setNewSetName((e.target as HTMLInputElement).value)}
                      placeholder="Geography Quiz"
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">Description</span>
                    </label>
                    <textarea
                      class="textarea textarea-bordered"
                      value={newSetDesc}
                      onInput={(e) =>
                        setNewSetDesc((e.target as HTMLTextAreaElement).value)}
                      placeholder="Optional description..."
                    />
                  </div>
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text">
                        Select Questions ({newSetQuestions.length} selected)
                      </span>
                    </label>
                    <div class="max-h-48 overflow-y-auto border rounded-lg p-2">
                      {questions.my.length === 0
                        ? (
                          <p class="text-sm opacity-60">
                            No questions yet. Create some first.
                          </p>
                        )
                        : (
                          questions.my.map((q) => (
                            <label
                              key={q.id}
                              class="flex items-center gap-2 p-2 hover:bg-base-200 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                class="checkbox checkbox-sm"
                                checked={newSetQuestions.includes(q.id)}
                                onChange={() => toggleQuestionInSet(q.id)}
                              />
                              <span class="text-sm truncate">{q.text}</span>
                            </label>
                          ))
                        )}
                    </div>
                  </div>
                  <div class="card-actions justify-end mt-4">
                    <button
                      type="button"
                      class="btn btn-ghost"
                      onClick={() => {
                        setShowNewSet(false);
                        setEditingSet(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary"
                      onClick={editingSet ? updateSet : createSet}
                    >
                      {editingSet ? "Save" : "Create"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div class="grid gap-4 md:grid-cols-2">
              {sets.my.map((s) => (
                <div key={s.id} class="card bg-base-100 shadow-lg">
                  <div class="card-body">
                    <h3 class="card-title">{s.name}</h3>
                    {s.description && <p class="opacity-60">{s.description}</p>}
                    <div class="flex items-center gap-2 text-sm opacity-60">
                      <span>{s.questionIds.length} questions</span>
                      {s.isPublic && (
                        <span class="badge badge-success badge-sm">Public</span>
                      )}
                    </div>
                    <div class="card-actions justify-end">
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        onClick={() =>
                          startEditSet(s)}
                      >
                        Edit
                      </button>
                      {!s.isPublic && (
                        <button
                          type="button"
                          class="btn btn-ghost btn-sm"
                          onClick={() => publishSet(s.id)}
                        >
                          Publish
                        </button>
                      )}
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm text-error"
                        onClick={() =>
                          deleteSet(s.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {sets.public.length > 0 && (
              <>
                <h2 class="text-xl font-bold mt-8">Public Sets</h2>
                <div class="grid gap-4 md:grid-cols-2">
                  {sets.public.map((s) => (
                    <div key={s.id} class="card bg-base-100 shadow-lg">
                      <div class="card-body">
                        <h3 class="card-title">{s.name}</h3>
                        {s.description && (
                          <p class="opacity-60">{s.description}</p>
                        )}
                        <div class="flex items-center gap-2 text-sm opacity-60">
                          <span>{s.questionIds.length} questions</span>
                          <span class="badge badge-success badge-sm">
                            Public
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
